import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as AscendingDescendingSortIcon } from "../../Assets/Icons/AscendingDescendingSortIcon.svg";
import {
  ContentWrapper,
  Count,
  FilterBox,
  Footer,
  ListContainer,
  MainContainer,
  Title,
  TopSection,
} from "./index.styled";
import { views } from "../../Configs/config";
import HospitalList from "../HospitalList/Index";
import OptionsList from "../OptionsList";
import { useSelector } from "react-redux";
import { useFilters } from "../../hooks/useFilters";
import { useResolvedStates } from "../../hooks/useResolvedStates";
import { TextSkeleton } from "../Skeletons/index.styled";
import ListViewSkeleton from "../Skeletons/Variants/ListViewSkeleton";
import {
  ListViewSkeletonContainer,
  SubTitle,
  Span,
} from "../HospitalList/HospitalListCard.styled";
import Info from "../Info";
import { toTitleCase } from "../../utils";
import { useQuery } from "@tanstack/react-query";
import { getTop25Hospitals } from "../../services/apiService";
import {ReactComponent as NoDataFound} from "../../Assets/Icons/NoDataFound.svg";
import { NoDataFoundContainer, NoDataFoundText } from "../CustomTable/index.styled";

// Sorting options
export const options = [
  { value: "ranking", label: "Ranking" },
  { value: "net_patient_revenue", label: "Net Patient Revenue" },
  { value: "calc_stats_beds_total", label: "No. of Beds" },
  // { value: "CMS Rating (>4)", label: "CMS Rating (>4)" },
  { value: "calc_stats_adj_pt_discharge", label: "Inpatient Discharges" },
  { value: "hosp10_name", label: "Hospital Name" },

  // { value: "Accreditations", label: "Accreditations" },
  // { value: "Ownership Type (For Profit)", label: "Ownership Type (For Profit)",// },
];

const RightSection = ({
  view,
  toggleState,
  totalHospitals,
}) => {
  const { dataRefetchTrigger } = useSelector((store) => store.app);
  const {
    partnerExclusiveFlag,
    globalFilter,
    quickFilter,
    legendFilter,
  } = useFilters();
  // A legend filter no longer stores its states in the URL, so resolve the
  // selected states (explicit `states` param OR legend-derived) centrally.
  const { effectiveStates } = useResolvedStates();
  const { specificStateData } = useSelector((store) => store.hospital);
  // Unified selection key: a single hexagon click sets one state, a legend
  // filter sets many. Empty means national (All States).
  const selectedStates = (effectiveStates || []).filter(Boolean);
  const statesKey = selectedStates.join(",");
  const dropdownRef = useRef(null);
  const [selectedOption, setSelectedOption] = useState(options[0].value);
  // Width the panel occupies while open (view === MAP). Captured at stable
  // (non-transitioning) moments so the inner content can keep this width while
  // the panel collapses to 0, clipping cleanly instead of reflowing.
  const [openWidth, setOpenWidth] = useState(null);
  const isTransitioningRef = useRef(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedState, setSelectedState] = useState(false);
  const [sortingLabel, setSortingLabel] = useState(options[0].label);
  const [sortOrder, setSortOrder] = useState("asc");
  const [isHovered, setIsHovered] = useState(false);
  
  const [isAtEnd, setIsAtEnd] = useState(false);
  const containerRef = useRef(null);
  const listContainerRef = useRef(null);

  // Top-25 list is fetched & cached by react-query, keyed on the active filter +
  // sort. Identical combinations are served from cache instead of refetching.
  const top25Params = useMemo(
    () => ({
      sortBy: selectedOption,
      sortOrder: selectedOption === "ranking" ? "asc" : sortOrder,
      partnerExclusiveFlag,
      globalFilter: globalFilter?.value,
      states: statesKey,
      quickFilter,
    }),
    [selectedOption, sortOrder, partnerExclusiveFlag, globalFilter, statesKey, quickFilter],
  );

  const {
    data: top25Data,
    isLoading: top25Loading,
    isFetching: top25Fetching,
    refetch: refetchTop25,
  } = useQuery({
    queryKey: ["top-25-hospitals", top25Params],
    queryFn: ({ signal }) => getTop25Hospitals(top25Params, signal),
  });

  const hospitalsList = top25Data?.data || [];
  // Single derived flag drives the three loader gates the manual fetch used to
  // toggle together (skeleton, main loader, list loader).
  const isTop25Loading = top25Loading || top25Fetching;
  const showSkelton = isTop25Loading;
  const showMainLoader = isTop25Loading;
  const showLoader = isTop25Loading;

  const checkIfAtEnd = () => {
    if (!listContainerRef.current) return;

    const scrollTop = listContainerRef.current.scrollTop;
    const scrollHeight = listContainerRef.current.scrollHeight;
    const clientHeight = listContainerRef.current.clientHeight;

    const buffer = 20;
    const reachedEnd = scrollHeight - scrollTop - clientHeight <= buffer;

    setIsAtEnd(reachedEnd);
  };

  // useEffect(() => {
  //   const q = searchParams.get(`q`);
  //   if (q === "TEXAS") {
  //     options[0] = {
  //       value: "net_patient_revenue",
  //       label: "Net Patient Revenue",
  //     };
  //     setSelectedOption("net_patient_revenue");
  //     setSortingLabel("Net Patient Revenue");
  //   } else {
  //     options[0] = {
  //       value: "net_patient_revenue",
  //       label: "Net Patient Revenue",
  //     };
  //     setSelectedOption("net_patient_revenue");
  //             setSortingLabel("Net Patient Revenue");
  //   }
  // }, [searchParams]);

  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.addEventListener("scroll", checkIfAtEnd);

      checkIfAtEnd();

      return () => {
        if (listContainerRef.current) {
          listContainerRef.current.removeEventListener("scroll", checkIfAtEnd);
        }
      };
    }
  }, []);

  useEffect(() => {
    setTimeout(checkIfAtEnd, 100);
  }, [hospitalsList]);

  // Capture the panel's open width only while it's stable (open and not
  // mid-transition). The inner content is locked to this width so a collapse
  // (35% -> 0) clips it cleanly rather than reflowing the title/list.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      if (view === views.MAP && !isTransitioningRef.current) {
        const w = el.getBoundingClientRect().width;
        if (w > 0) setOpenWidth(w);
      }
    };

    const handleTransitionStart = (e) => {
      if (e.propertyName === "width") isTransitioningRef.current = true;
    };
    const handleTransitionEnd = (e) => {
      if (e.propertyName === "width") {
        isTransitioningRef.current = false;
        measure();
      }
    };

    el.addEventListener("transitionstart", handleTransitionStart);
    el.addEventListener("transitionend", handleTransitionEnd);
    window.addEventListener("resize", measure);
    measure();

    return () => {
      el.removeEventListener("transitionstart", handleTransitionStart);
      el.removeEventListener("transitionend", handleTransitionEnd);
      window.removeEventListener("resize", measure);
    };
  }, [view]);

  // Handle sorting change
  const handleOptionClick = (option) => {
    setSelectedOption(option.value);
    setSortingLabel(option.label);

    if (option.value === "hosp10_name") {
      setSortOrder("asc");
    } else {
      setSortOrder("desc");
      // setSortOrder((prevOrder) => prevOrder);
    }

    setIsDropdownOpen(!isDropdownOpen);
  };

  // Monitor state selection to toggle the header state.
  useEffect(() => {
    setSelectedState(selectedStates.length > 0);
  }, [statesKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    //if (!selectedState) dispatch(resetSelectedState());
  }, [selectedState]);

  useEffect(() => {
    if (dataRefetchTrigger) {
      refetchTop25();
    }
  }, [dataRefetchTrigger]);

  // Header label when no single state is selected: reflect a legend selection
  // (e.g. "High Density States") instead of misleadingly showing "All States".
  const noStateHeader =
    selectedStates.length > 0
      ? legendFilter
        ? legendFilter === "partner"
          ? "Partner States"
          : `${toTitleCase(legendFilter)} Density States`
        : "Selected States"
      : "All States";

  return (
    <MainContainer view={view} ref={containerRef} isAtEnd={!isAtEnd}>
      <ContentWrapper openWidth={openWidth}>
        <TopSection>
          <Title>
           {showLoader ? (
            <TextSkeleton width={25} />
              ) : (
                <div>
                  <Title>
                    Top <Count>25</Count> Hospitals in{" "}
                    {Object.keys(specificStateData)?.length > 0
                      ? toTitleCase(specificStateData.state)
                      : legendFilter
                        ? legendFilter === "partner"
                          ? "Partner ESA"
                          : `${toTitleCase(legendFilter)} Density States`
                        : "the US"}
                  </Title>

                  <SubTitle>
                    Sorted by {sortingLabel}
                  </SubTitle>
                </div>
            )}
          </Title>
          <FilterBox
            ref={dropdownRef}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            isDropdownOpen={isDropdownOpen}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            isHovered={isHovered}
          >
            <Info
              showArrow={false}
              config={{
                tooltipText: "Sorting Options",
                position: "top",
              }}
              position="start"
            >
              <AscendingDescendingSortIcon
                style={{
                  fill: isHovered ? "#F96C50" : "#333333",
                }}
              />
            </Info>
            <OptionsList
              options={options}
              onOptionClick={handleOptionClick}
              selectedOption={selectedOption}
              customWidth={16.5}
              top={3.4}
              right={true}
              // onPreventToggle={onPreventToggle}
              showTitle={"Sort"}
              showIcon={true}
              customPadding={0.313}
              customBorderRadius={0.625}
              isOpen={isDropdownOpen}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
              isSortingDisabled={false}
              isSelectModelOption={true}
            />
          </FilterBox>
        </TopSection>

        <ListContainer key={selectedOption} ref={listContainerRef}>
          {showSkelton ||
          hospitalsList?.length === undefined ||
          showMainLoader ? (
            <ListViewSkeletonContainer>
              {Array(7)
                .fill(null)
                .map((_, index) => (
                  <ListViewSkeleton key={index} />
                ))}
            </ListViewSkeletonContainer>
          ) : hospitalsList?.length > 0 ? (
            <HospitalList
                hospitalsList={hospitalsList}
                sortingLabel={sortingLabel}
                toggleState={toggleState}
              />
          ) : (
            <NoDataFoundContainer>
              <NoDataFound/>
              <NoDataFoundText>
                  No Data Available
              </NoDataFoundText>
            </NoDataFoundContainer>
          )}
        </ListContainer>
      </ContentWrapper>
    </MainContainer>
  );
};

export default RightSection;
